/**
 * Outcome icons — the win/loss marks. The minimal standard pair carries W/L
 * on stats surfaces: planted with a waving swallowtail (win) vs the same
 * standard mid-fall (loss) — the topple vocabulary deliberately rhymes with
 * the bidding pass topple, where a fallen standard already means defeat. The
 * ruin is the declarer-loss outcome image — the result scene's loss backdrop
 * and the outcome board beat's end-swap frame (the win side is the claimed
 * gatehouse staying standing, its cloth flying as the beat's pennant).
 * Rationale: docs/components/ICONOGRAPHY.md § Outcome Icons.
 *
 * All factories draw with currentColor so the host context recolors them —
 * stats chips inherit their surface's text color. The standards additionally
 * take an optional identity clothColor (the result verdict passes the round
 * protagonist's color): cloth carries identity, hardware stays neutral — the
 * same contract as createBattleStandard (ICONOGRAPHY § Coloring on role
 * icons). The ruin carries createGatehouseClaimed's full contract: structure
 * in identity strokes (vellum-rimmed when dark), cloth as identity fill +
 * rim, the toppled pole neutral.
 *
 * The RUIN_* geometry constants are exported as the outcome board beat's
 * morph targets (animation/outcome-board-beat.js): the beat animates the
 * claimed gatehouse INTO these shapes, then end-swaps to this factory — one
 * source for the shapes keeps that swap seamless by construction.
 */
import { svg, path, filledDot } from "./svg.js";
import {
  addSwallowtailCloth,
  appendClaimedBanner,
  appendGatehouseWalls,
  appendPortcullis,
  needsRim,
} from "./role-icons.js";
import { NEUTRAL_CLOTH } from "../constants.js";

// Swallowtail with a wave in the cloth (the planted standard's).
const UP_CLOTH = "M14 9 Q26 9.5 38 11.5 L31 16 L38 20.5 Q26 22.5 14 23 Z";
// Cloth hanging straight down from the upper pole — gravity has it now. The
// mono (stroke) variant deliberately leaves the pole edge open; the identity
// variant closes it so the fill outline reads as one cloth.
const DOWN_CLOTH = "M35.5 20.5 L35.5 33 L31 29.5 L26.5 33 L28 25.5";

/** Win — standard planted, orb finial (the battle standard's signature),
 *  proud waving cloth. Ground is a short strip centered under the pole —
 *  a full-width line competed with the cloth for horizontal weight. */
export function createOutcomeStandardUp(size, clothColor) {
  const el = svg(size, size);
  el.appendChild(path("M9 43 L19 43", 2));
  el.appendChild(path("M14 43 L14 8", 2.5));
  el.appendChild(filledDot(14, 5.5, 2));
  if (clothColor) addSwallowtailCloth(el, UP_CLOTH, clothColor, 2);
  else el.appendChild(path(UP_CLOTH, 2));
  return el;
}

/** Loss — the same standard mid-fall: pole at ~45°, the cloth swung loose and
 *  hanging straight down under gravity (the cue that reads "falling" where a
 *  flat 2D pole-on-the-ground never did). No ground line — it competed with
 *  the fallen pole for the same horizontal. */
export function createOutcomeStandardDown(size, clothColor) {
  const el = svg(size, size);
  // Pole well past the balance point, falling right
  el.appendChild(path("M8 40 L39 18", 2.5));
  el.appendChild(filledDot(40.5, 16.9, 2));
  if (clothColor) addSwallowtailCloth(el, DOWN_CLOTH + " Z", clothColor, 2);
  else el.appendChild(path(DOWN_CLOTH, 2));
  // Rotation-trail arcs behind the fall, concentric around the pole base
  el.appendChild(path("M14 11 A 30 30 0 0 1 23 14", 1.4));
  el.appendChild(path("M14 17 A 24 24 0 0 1 20.5 19.5", 1.4));
  return el;
}

// --- Ruin geometry (the board beat's morph targets) ---
// Left half stands; the right tower has crumbled stair-step down.
export const RUIN_WALL_D =
  "M8 38 L8 20 L14 20 L14 12 L22 12 L22 20 L26 20 L29 16 L31 23 L36 19 L38 26 L40 38";
// Crack down the (surviving) left wall — the beat DrawSVGs this onto the
// intact gatehouse before the collapse, so crack and ruin share one shape.
export const RUIN_CRACK_D = "M12 38 L14 31 L11 27 L13 23";
// Cloth drooping limp from the fallen tip. Open for the mono stroke (the pole
// edge deliberately open), closed for the identity fill — the same split as
// DOWN_CLOTH above.
const RUIN_CLOTH_OPEN_D = "M45 24 L45 32 L41.5 29 L38 32 L38 26";
export const RUIN_CLOTH_D = RUIN_CLOTH_OPEN_D + " Z";
// Rubble dots at the base: [cx, cy, r].
export const RUIN_RUBBLE = [
  [34, 36, 1.5],
  [39, 35, 2],
  [44, 37, 1.3],
];

// The flying pennant — the win beat's triumph shape at rest. Lives here (not
// in the beat) so the factory below is the single source and the beat's
// morph target can't drift from the canonical rest frame it end-swaps to.
export const PENNANT_CLOTH_D = "M37 -5 L47 -2.5 L43 0.5 L47 3.5 L37 6 Z";

/** Declarer win — the claimed gatehouse standing proud, its cloth flying as
 *  the pennant. The win-side sibling of the ruin below: the outcome board
 *  beat's end-swap frame and the FINISHED board's declarer-victory watermark.
 *  Composed from the role-icon factory's own builders, so it carries the
 *  claimed gatehouse's full identity contract (structure strokes + portcullis
 *  in identity with vellum rims when dark, cloth as identity fill + rim,
 *  banner pole + hardware neutral). */
export function createGatehousePennant(size, identityColor) {
  const el = svg(size, size, "0 -10 48 58");
  appendGatehouseWalls(el, identityColor);
  appendPortcullis(el, identityColor);
  appendClaimedBanner(el);
  addSwallowtailCloth(el, PENNANT_CLOTH_D, identityColor ?? NEUTRAL_CLOTH, 0.8);
  return el;
}

/** Declarer loss — the gatehouse in ruins, banner toppled against the rubble.
 *  The result scene's loss watermark; no standalone W/L duty. The
 *  viewBox extends upward like createGatehouseClaimed's, leaving headroom the
 *  banner pole needs. `identityColor` applies the claimed gatehouse's contract
 *  (the fallen fortress is still the declarer's): structure strokes + rubble in
 *  identity (vellum rims when dark, per `needsRim`), cloth as identity fill +
 *  vellum rim, the toppled pole neutral (frame=vellum). Omitted → entirely
 *  `currentColor`, the original chip-grade mono. */
export function createOutcomeRuin(size, identityColor) {
  const el = svg(size, size, "0 -10 48 58");
  const structure = (d, w) => {
    if (needsRim(identityColor)) {
      const rim = path(d, w + 1.5);
      rim.style.stroke = "#E2E8F0";
      el.appendChild(rim);
    }
    const p = path(d, w);
    if (identityColor) p.style.stroke = identityColor;
    el.appendChild(p);
    return p;
  };
  structure(RUIN_WALL_D, 2.5);
  // Arch survives
  structure("M20 38 L20 30 Q24 26 28 30 L28 38", 2);
  structure(RUIN_CRACK_D, 1.2);
  // Toppled banner pole collapsed over the breach, tip down
  el.appendChild(path("M29 12 L45 24", 2));
  if (identityColor) {
    addSwallowtailCloth(el, RUIN_CLOTH_D, identityColor, 0.8);
  } else {
    el.appendChild(path(RUIN_CLOTH_OPEN_D, 1.8));
  }
  for (const [cx, cy, r] of RUIN_RUBBLE) {
    const dot = filledDot(cx, cy, r);
    if (identityColor) dot.style.fill = identityColor;
    el.appendChild(dot);
  }
  return el;
}
